import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";

import {
  COLORS,
  CONSTANTS,
  FONTSIZE,
  FONTWEIGHT
} from "helpers/designConstants";

import useArticleSearchReader from "database/articles/useArticleSearchReader";
import NestedNav from "components/shared/nestedNavigation/NestedNav";
import { useArticleReader } from "database/articles/useArticleReader";

import ArticleCard from "components/shared/ArticleCard";
import useArticleZoomModal from "components/shared/articleZoomModal/useArticleZoomModal";
import ArticleZoomModal from "components/shared/articleZoomModal/ArticleZoomModal";
import useInventoryModal from "components/shared/inventoryModal/useInventoryModal";
import InventoryModal from "components/shared/inventoryModal/InventoryModal";
import { MiniButton } from "components/shared/standardElements";
import EditArticleQtysModal from "components/shared/editArticleQtysModal/EditArticleQtysModal";
import Loading from "components/shared/Loading";
import { flattenDeep, xor, isEqual } from "lodash";

const ProductPickerScrollArea = ({
  pricingToShowPreview,
  pricingToShowOrdering,
  allowMultipleSeasons = false,
  allowOrderEntry = true,
  showInventoryButton,
  selectedSeasonKey,
  selectedSeasonSiloKey,
  articleSort,
  searchResults,
  selectedArticles = [],
  onArticleSelection,
  onAddArticlesFromGroup,
  onRemoveArticlesFromCollection,
  showCollectionHeader,
  formControl,
  getFormValues,
  setFormValue,
  overallCategorySummary,
  onSeasonChange,
  renderSearchResults,
  searchStatus
}) => {
  //HOOKS
  const { t, i18n } = useTranslation();
  const { searchByCategoryKey, searchByCollectionKey } =
    useArticleSearchReader();
  const {
    activeSeasons,
    schemas,
    getCollectionKeysForSeason,
    getGeneralCategoryKeysForSeason
  } = useArticleReader();
  const {
    toggleArticleZoomModal,
    isZoomModalShowing,
    onZoomArticle,
    zoomArticle,
    zoomArticleImages
  } = useArticleZoomModal();
  const {
    isInventoryModalShowing,
    toggleInventoryModal,
    inventoryArticle,
    onViewInventory
  } = useInventoryModal();

  //STATE
  const [selectedSeason, setSelectedSeason] = useState();
  const [selectedSeasonSilo, setSelectedSeasonSilo] = useState();
  const [browsingSeason, setBrowsingSeason] = useState();
  const [navLocation, setNavLocation] = useState([]);
  const [visibleArticles, setVisibleArticles] = useState();
  const [allArticleGroupKeys, setAllArticleGroupKeys] = useState([]);
  const [initialSelection, setInitialSelection] = useState();
  const [articleIDForOrderEdit, setArticleIDForOrderEdit] = useState();

  const articleScrollContainer = useRef();

  // For multi-season mode: the browsing season key determines which schema to show
  const effectiveSeasonKey = allowMultipleSeasons
    ? browsingSeason?.key || selectedSeasonKey
    : selectedSeasonKey;

  // Initialize browsingSeason for multi-season mode
  useEffect(() => {
    if (!allowMultipleSeasons || activeSeasons == null || selectedSeasonKey == null) return;
    if (browsingSeason == null) {
      const season = activeSeasons.find((s) => s.key === selectedSeasonKey) ||
        activeSeasons[activeSeasons.length - 1];
      setBrowsingSeason(season);
    }
  }, [allowMultipleSeasons, activeSeasons, selectedSeasonKey, browsingSeason]);

  useEffect(() => {
    if (
      activeSeasons == null ||
      Object.keys(schemas).length === 0 ||
      selectedSeasonKey == null
    )
      return;
    setSelectedSeason(
      activeSeasons.filter((season) => season.key === selectedSeasonKey)[0]
    );

    if (!allowMultipleSeasons) {
      let selectedSchema = [];
      if (articleSort === "collection") {
        selectedSchema = schemas?.[selectedSeasonKey]?.schemas;
      } else if (articleSort === "category") {
        selectedSchema = schemas?.[selectedSeasonKey]?.categorySchemas;
      }

      setSelectedSeasonSilo(
        selectedSchema?.filter((schema) => schema.key === selectedSeasonSiloKey)[0]
      );
    }
  }, [
    activeSeasons,
    allowMultipleSeasons,
    articleSort,
    schemas,
    selectedSeasonKey,
    selectedSeasonSiloKey
  ]);

  useEffect(() => {
    if (effectiveSeasonKey == null || articleSort == null) return;
    if (articleSort === "collection") {
      getCollectionKeysForSeason(effectiveSeasonKey).then(
        (seasonCollectionKeys) => {
          setAllArticleGroupKeys(seasonCollectionKeys);
        }
      );
    } else if (articleSort === "category") {
      getGeneralCategoryKeysForSeason(effectiveSeasonKey).then(
        (generalCategoryKeys) => {
          setAllArticleGroupKeys(generalCategoryKeys);
        }
      );
    }
  }, [
    getCollectionKeysForSeason,
    getGeneralCategoryKeysForSeason,
    effectiveSeasonKey,
    articleSort
  ]);

  const userMadeSelection = useCallback(
    async (item) => {
      if (articleSort === "collection") {
        const result = await searchByCollectionKey(
          effectiveSeasonKey,
          item?.key,
          true
        );
        setVisibleArticles(result);
      } else if (articleSort === "category") {
        const result = await searchByCategoryKey(
          effectiveSeasonKey,
          item?.key,
          true
        );
        setVisibleArticles(result);
      }
    },
    [articleSort, searchByCategoryKey, searchByCollectionKey, effectiveSeasonKey]
  );

  const onAddArticlesInGroup = async (key) => {
    onAddArticlesFromGroup(key);
  };

  const onRemoveArticlesInCollection = async (key) => {
    onRemoveArticlesFromCollection(key);
  };

  const onUpdateCurrentNavigationLocation = useCallback(
    (location) => {
      if (schemas == null || effectiveSeasonKey == null) return;

      let schema = [];
      if (articleSort === "collection") {
        schema = schemas?.[effectiveSeasonKey]?.schemas;
      } else if (articleSort === "category") {
        schema = schemas?.[effectiveSeasonKey]?.categorySchemas;
      }

      let composedBreadCrumbs = [];
      let currentDepthData;
      location?.forEach((selection, depth) => {
        if (depth === 0) {
          currentDepthData = allowMultipleSeasons
            ? schema?.[selection]
            : schema?.find((s) => s.key === selectedSeasonSiloKey) ||
              schema?.[selection];
        } else {
          if (currentDepthData?.groups && selection < currentDepthData.groups.length) {
            currentDepthData = currentDepthData.groups[selection];
          }
        }
        composedBreadCrumbs.push(
          i18n.language === "en"
            ? currentDepthData?.name
            : currentDepthData?.name_fr
        );
        if (depth !== location.length - 1) composedBreadCrumbs.push(" ‣ ");
      });

      setNavLocation(composedBreadCrumbs);
    },
    [articleSort, allowMultipleSeasons, i18n.language, schemas, effectiveSeasonKey, selectedSeasonSiloKey]
  );

  useEffect(() => {
    if (searchResults) {
      // For multi-season mode, searchResults is a segmented object — don't set visibleArticles
      if (!allowMultipleSeasons) {
        setVisibleArticles(searchResults);
      }
    }
  }, [searchResults, allowMultipleSeasons]);

  // Handle season change in multi-season mode
  const handleSeasonClick = useCallback(
    (season) => {
      setBrowsingSeason(season);
      setVisibleArticles(null);
      setInitialSelection(null);
      onSeasonChange?.(season.key);
    },
    [onSeasonChange]
  );

  //Get the initial selection
  //Iterate through the nesting and pick the first item in each level
  useEffect(() => {
    if (Object.keys(schemas).length === 0 || activeSeasons == null) return;

    if (allowMultipleSeasons) {
      // Multi-season mode: use full schema for browsing season
      if (effectiveSeasonKey == null || schemas[effectiveSeasonKey] == null) return;
      if (!searchResults) {
        const schemaList = articleSort === "collection"
          ? schemas[effectiveSeasonKey]?.schemas
          : schemas[effectiveSeasonKey]?.categorySchemas;
        if (schemaList == null || schemaList.length === 0) return;

        let isBottom = false;
        let selection = [];
        let currentDepth = schemaList[0];
        while (!isBottom) {
          if (currentDepth?.groups != null) {
            selection.push(0);
            currentDepth = currentDepth?.groups[0];
          } else {
            selection.push(0);
            isBottom = true;
            userMadeSelection(currentDepth);
          }
        }
        setInitialSelection(selection);
      }
    } else {
      // Single-season mode: use selectedSeasonSilo
      if (selectedSeasonSilo == null) return;
      if (!searchResults) {
        let isBottom = false;
        let selection = [];
        let currentDepth = selectedSeasonSilo;
        while (!isBottom) {
          if (currentDepth?.groups != null) {
            selection.push(0);
            currentDepth = currentDepth?.groups[0];
          } else {
            selection.push(0);
            isBottom = true;
            userMadeSelection(currentDepth);
          }
        }
        setInitialSelection(selection);
      }
    }
  }, [
    userMadeSelection,
    selectedSeasonSilo,
    searchResults,
    schemas,
    activeSeasons,
    allowMultipleSeasons,
    effectiveSeasonKey,
    articleSort
  ]);

  //Reset article scroll container to top on new sidebar selection
  useEffect(() => {
    if (!searchResults && articleScrollContainer?.current != null) {
      articleScrollContainer.current.scrollTop = 0;
    }
  }, [visibleArticles, searchResults]);

  // Seasons newest-first for display, to match the public viewer's season ordering.
  // (activeSeasons is chronological ascending; the auto-select still uses it as-is.)
  const seasonsNewestFirst = useMemo(
    () => [...(activeSeasons || [])].reverse(),
    [activeSeasons]
  );

  // Article numbers already in the catalogue via a DIFFERENT record. Adding a sibling is legitimate
  // (two seasons, two entries) but must not happen silently — the public page would show the
  // product twice, at two prices.
  const selectedArticleNumbersById = useMemo(() => {
    const map = new Map();
    (selectedArticles || []).forEach((article) => {
      map.set(article.id, article.articleNumber);
    });
    return map;
  }, [selectedArticles]);

  // Determine the nav list map based on mode
  const navListMap = allowMultipleSeasons
    ? (articleSort === "collection"
        ? schemas?.[effectiveSeasonKey]?.schemas
        : schemas?.[effectiveSeasonKey]?.categorySchemas) || []
    : [selectedSeasonSilo];

  // Check if component is ready to render
  const isLoading = allowMultipleSeasons
    ? schemas == null || activeSeasons == null || Object.keys(schemas).length === 0
    : schemas == null || activeSeasons == null || selectedSeason == null || selectedSeasonSilo == null;

  if (isLoading) return <Loading />;

  return (
    <Wrapper shouldHideMenu={searchResults != null}>
      <LeftSideBar shouldHide={searchResults != null}>
        <SeasonPicker>
          <ul>
            {allowMultipleSeasons ? (
              seasonsNewestFirst.map((season) => (
                <SeasonListItem
                  key={season.key}
                  isSelected={season.key === effectiveSeasonKey}
                  onClick={() => handleSeasonClick(season)}
                >
                  {i18n.language === "en" ? season.name : season.name_fr}
                </SeasonListItem>
              ))
            ) : (
              <SeasonListItem key={selectedSeasonKey} isSelected={true}>
                {i18n.language === "en"
                  ? selectedSeason?.name
                  : selectedSeason?.name_fr}
              </SeasonListItem>
            )}
          </ul>
        </SeasonPicker>
        <NestedNav
          userSelectedItem={userMadeSelection}
          onUpdateCurrentNavigationLocation={onUpdateCurrentNavigationLocation}
          navListMap={navListMap}
          initialSelection={initialSelection}
          backgroundColor={"white"}
          onAddArticlesInGroup={onAddArticlesInGroup}
          onRemoveArticlesInCollection={onRemoveArticlesInCollection}
          currentlySelectedArticleGroupKeys={
            articleSort === "collection"
              ? flattenDeep(
                  selectedArticles.map(
                    (article) => article[`${effectiveSeasonKey}_collectionKeys`]
                  )
                ).filter(Boolean)
              : selectedArticles.map(
                  (article) =>
                    article[`${effectiveSeasonKey}_generalCategoryKey`]
                ).filter(Boolean)
          }
          seasonArticleGroupKeys={allArticleGroupKeys}
        />
      </LeftSideBar>
      <CollectionSectionBody ref={articleScrollContainer}>
        <SectionHeader>
          {searchResults != null && searchStatus ? (
            searchStatus
          ) : (
            <HeaderBreadCrumbs>
              {showCollectionHeader && searchResults == null
                ? navLocation.map((text, index) => (
                    <span key={`crubms-${index}`}>{text}</span>
                  ))
                : searchResults != null
                ? "Performing Search..."
                : ""}
            </HeaderBreadCrumbs>
          )}
        </SectionHeader>
        {/* Search results rendering is supplied by the catalogue picker, so this shared component
            never imports catalogue-only code. Booking passes no renderSearchResults and is
            unaffected. */}
        {allowMultipleSeasons && searchResults ? (
          renderSearchResults?.({ onZoomArticle, onViewInventory })
        ) : (
          <ScrollBody>
            {visibleArticles?.map((article) => {
              const hasSibling =
                !selectedArticleNumbersById.has(article.id) &&
                [...selectedArticleNumbersById.values()].includes(
                  article.articleNumber
                );
              return (
                <BrowseCardWrapper key={article.articleNumber}>
                  <FillingArticleCard
                    seasonKey={effectiveSeasonKey}
                    priceToShow={pricingToShowPreview}
                    article={article}
                    showEditButton={allowOrderEntry}
                    showInventoryButton={showInventoryButton}
                    shouldShowLimitedSoldOut
                    isSelectable
                    isSelected={selectedArticles.some(
                      (selectedArticle) => selectedArticle.id === article.id
                    )}
                    didSelectAddRemove={onArticleSelection}
                    onZoomArticle={onZoomArticle}
                    onViewInventory={onViewInventory}
                    didSelectEditQuantities={setArticleIDForOrderEdit}
                  />
                  {allowMultipleSeasons && hasSibling && (
                    <SiblingNote>
                      {t(
                        "sibling_season_added",
                        "Another season of this product is already in the catalogue"
                      )}
                    </SiblingNote>
                  )}
                </BrowseCardWrapper>
              );
            })}
          </ScrollBody>
        )}
      </CollectionSectionBody>
      <ArticleZoomModal
        show={isZoomModalShowing}
        onClose={toggleArticleZoomModal}
        imageList={zoomArticleImages}
        article={zoomArticle}
      />
      {showInventoryButton && (
        <InventoryModal
          show={isInventoryModalShowing}
          onClose={toggleInventoryModal}
          article={inventoryArticle}
        />
      )}
      {allowOrderEntry && (
        <EditArticleQtysModal
          show={articleIDForOrderEdit != null}
          onClose={() => setArticleIDForOrderEdit()}
          articleID={articleIDForOrderEdit}
          articleDetails={
            visibleArticles?.filter(
              (article) => article.id === articleIDForOrderEdit
            )[0]
          }
          formControl={formControl}
          getFormValues={getFormValues}
          setFormValue={setFormValue}
          overallCategorySummary={overallCategorySummary}
          displayPrice={pricingToShowOrdering}
          seasonKey={effectiveSeasonKey}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  height: 100%;
  overflow: hidden;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  position: relative;

  display: flex;
`;

const LeftSideBar = styled.div`
  flex-shrink: ${({ shouldHide }) => (shouldHide ? 100 : 0)};
  height: 100%;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  border-right: 1px solid ${COLORS.secondary};
  transition: flex-shrink 0.6s ease-in-out;
`;

const SeasonPicker = styled.div`
  background: ${COLORS.tertiary};
  border-bottom: 1px solid ${COLORS.secondary};

  ul {
    margin: 0;
    padding: 0;
    list-style-type: none;
  }
`;

const SeasonListItem = styled.li`
  margin: 5px;
  padding: 10px;
  font-weight: ${FONTWEIGHT.heavy};
  font-size: ${FONTSIZE.medium};
  text-transform: uppercase;
  cursor: pointer;
  color: ${({ isSelected }) =>
    isSelected ? COLORS.primary : COLORS.secondary};
  background: ${({ isSelected }) => isSelected && COLORS.secondary};
  border-radius: 4px;
`;

const CollectionSectionBody = styled.div`
  flex-grow: 100;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: grid;
  grid-template-rows: min-content 1fr;
  background: ${COLORS.tertiary};
  transition: all 0.6s ease-in-out;
`;

const ScrollBody = styled.div`
  padding: 3px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 0.33333fr));
  /* grid-template-rows: min-content 1fr; */
  grid-gap: 3px;
  align-content: start;
  /* background: ${COLORS.tertiary}; */
  /* position: relative; */
`;

const SectionHeader = styled.div`
  grid-column: 1/-1;
  z-index: 15;
  width: 100%;
  padding: 12px 5px;
  background-color: ${COLORS.secondary};
  border-top-right-radius: 4px;
  position: sticky;
  top: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0px 2px 5px lightgrey;
`;

// const HeaderBreadCrumbs = styled.div`
//   color: ${COLORS.text};
//   text-transform: uppercase;
//   font-size: ${FONTSIZE.xsmall};
//   font-weight: ${FONTWEIGHT.heavy};
//   padding-left: 10px;
// `;

const HeaderBreadCrumbs = styled.div`
  color: ${COLORS.text};
  text-transform: uppercase;
  font-size: ${FONTSIZE.small};
  font-weight: ${FONTWEIGHT.heavy};
  padding-left: 10px;
  display: flex;
  align-items: center;

  span {
    padding: 3px;
    :last-child {
      color: ${COLORS.primary};
      font-size: ${FONTSIZE.medium};
    }
  }
`;

const BrowseCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

// Wrapping the card cost it the grid's automatic row-height stretch, so it has to grow explicitly.
// Composed via styled() so ArticleCard itself — five consumers — stays untouched.
const FillingArticleCard = styled(ArticleCard)`
  flex: 1 1 auto;
`;

const SiblingNote = styled.div`
  padding: 3px 6px;
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.alertOrange};
  text-transform: uppercase;
`;

const HeaderButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledMiniButton = styled(MiniButton)`
  margin-left: 10px;
`;

export default ProductPickerScrollArea;
